import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldVirus,
  faBan,
  faCheck,
  faTimes,
  faThumbsUp,
  faPlay,
  faUsers,
  faChevronRight,
  faChevronDown,
  faChevronLeft,
  faClipboardCheck,
  faList,
  faFlag,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import "../css/Common.css";
import "./css/AdminBlocklistReview.css";
import AdminBlocklistAPI from "../services/AdminBlocklistAPI";
import LoadingSpinner from "../common/LoadingSpinner";
import RefreshButton from "../common/RefreshButton";

const PAGE_SIZE = 25;

/**
 * Platform Admin page backing the weekly blocklist review.
 *
 * Shows domains the weekly batch proposed as malicious (engine re-scan, gated by
 * ≥1% of active/subscribed users reporting) for the admin to confirm/reject, and
 * user "actually safe" corrections for the admin to clear. Nothing is
 * auto-blocked or auto-cleared — every change here is an explicit admin action.
 *
 * Alongside the review queue, two browsing tabs let the admin view every
 * blocklist entry and every reported URL, each with filters.
 */
function AdminBlocklistReview() {
  const [activeTab, setActiveTab] = useState("review");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyDomain, setBusyDomain] = useState(null);
  const [expanded, setExpanded] = useState({});

  // "All blocklist" tab state
  const [blocklist, setBlocklist] = useState(null);
  const [blocklistLoading, setBlocklistLoading] = useState(false);
  const [blocklistPage, setBlocklistPage] = useState(0);
  const [blocklistFilters, setBlocklistFilters] = useState({
    active: "",
    source: "",
    search: "",
  });
  const [blocklistSearchInput, setBlocklistSearchInput] = useState("");

  // "Reported URLs" tab state
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsPage, setReportsPage] = useState(0);
  const [reportsFilters, setReportsFilters] = useState({
    status: "",
    reportType: "",
    search: "",
  });
  const [reportsSearchInput, setReportsSearchInput] = useState("");

  const toggleExpanded = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const loadReview = useCallback(async () => {
    try {
      const response = await AdminBlocklistAPI.getReview();
      setData(response || {});
    } catch (err) {
      toast.error("Failed to load blocklist review");
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadBlocklist = useCallback(async () => {
    setBlocklistLoading(true);
    try {
      const response = await AdminBlocklistAPI.getBlocklistEntries({
        page: blocklistPage,
        size: PAGE_SIZE,
        active: blocklistFilters.active,
        source: blocklistFilters.source,
        search: blocklistFilters.search,
      });
      setBlocklist(response || {});
    } catch (err) {
      toast.error("Failed to load blocklist entries");
      console.error(err);
    } finally {
      setBlocklistLoading(false);
    }
  }, [blocklistPage, blocklistFilters]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const response = await AdminBlocklistAPI.getReports({
        page: reportsPage,
        size: PAGE_SIZE,
        status: reportsFilters.status,
        reportType: reportsFilters.reportType,
        search: reportsFilters.search,
      });
      setReports(response || {});
    } catch (err) {
      toast.error("Failed to load reported URLs");
      console.error(err);
    } finally {
      setReportsLoading(false);
    }
  }, [reportsPage, reportsFilters]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  useEffect(() => {
    if (activeTab === "blocklist") loadBlocklist();
  }, [activeTab, loadBlocklist]);

  useEffect(() => {
    if (activeTab === "reports") loadReports();
  }, [activeTab, loadReports]);

  const handleRefresh = () => {
    if (activeTab === "blocklist") {
      loadBlocklist();
      return;
    }
    if (activeTab === "reports") {
      loadReports();
      return;
    }
    setRefreshing(true);
    loadReview();
  };

  const runAction = async (fn, domain, successMsg) => {
    setBusyDomain(domain);
    try {
      await fn(domain);
      toast.success(successMsg);
      await loadReview();
    } catch (err) {
      toast.error(err?.message || "Action failed");
      console.error(err);
    } finally {
      setBusyDomain(null);
    }
  };

  const handleRunBatch = async () => {
    setRefreshing(true);
    try {
      const summary = await AdminBlocklistAPI.runBatch();
      toast.success(
        `Batch complete — ${summary?.proposedBad ?? 0} proposed bad, ` +
          `${summary?.falsePositiveCorrections ?? 0} corrections`,
      );
      await loadReview();
    } catch (err) {
      toast.error("Failed to run batch");
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  // ── Blocklist tab filter/pagination handlers ──────────────────────────────
  const applyBlocklistFilter = (patch) => {
    setBlocklistPage(0);
    setBlocklistFilters((prev) => ({ ...prev, ...patch }));
  };
  const submitBlocklistSearch = (e) => {
    e.preventDefault();
    applyBlocklistFilter({ search: blocklistSearchInput.trim() });
  };
  const resetBlocklistFilters = () => {
    setBlocklistSearchInput("");
    setBlocklistPage(0);
    setBlocklistFilters({ active: "", source: "", search: "" });
  };

  // ── Reported-URLs tab filter/pagination handlers ──────────────────────────
  const applyReportsFilter = (patch) => {
    setReportsPage(0);
    setReportsFilters((prev) => ({ ...prev, ...patch }));
  };
  const submitReportsSearch = (e) => {
    e.preventDefault();
    applyReportsFilter({ search: reportsSearchInput.trim() });
  };
  const resetReportsFilters = () => {
    setReportsSearchInput("");
    setReportsPage(0);
    setReportsFilters({ status: "", reportType: "", search: "" });
  };

  if (loading) {
    return (
      <div className="blocklist-review">
        <LoadingSpinner message="Loading blocklist review..." />
      </div>
    );
  }

  const proposedBad = data?.proposedBad || [];
  const falsePositives = data?.falsePositives || [];

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const renderDetailRow = (row, keyPrefix, colSpan) => {
    const key = `${keyPrefix}:${row.domain}`;
    if (!expanded[key]) return null;
    const reports = row.reports || [];
    return (
      <tr className="blr-detail-row" key={`${key}-detail`}>
        <td colSpan={colSpan}>
          {reports.length === 0 ? (
            <div className="blr-detail-empty">
              No individual report details.
            </div>
          ) : (
            <table className="blr-subtable">
              <thead>
                <tr>
                  <th>Reported URL</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Reason</th>
                  <th>Reported</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td className="blr-sub-url" title={r.url}>
                      {r.url || "—"}
                    </td>
                    <td>{r.reportType || "—"}</td>
                    <td>{r.sourceChannel || "—"}</td>
                    <td className="blr-sub-reason">{r.reason || "—"}</td>
                    <td>{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </td>
      </tr>
    );
  };

  const renderPagination = (pageData, currentPage, onPage, busy) => {
    const totalPages = pageData?.totalPages ?? 0;
    const totalElements = pageData?.totalElements ?? 0;
    if (totalElements === 0) return null;
    return (
      <div className="blr-pagination">
        <span className="blr-pagination-info">
          Page {currentPage + 1} of {Math.max(totalPages, 1)} · {totalElements}{" "}
          total
        </span>
        <div className="blr-pagination-controls">
          <button
            className="blr-page-btn"
            disabled={busy || currentPage <= 0}
            onClick={() => onPage(currentPage - 1)}
            aria-label="Previous page"
          >
            <FontAwesomeIcon icon={faChevronLeft} /> Prev
          </button>
          <button
            className="blr-page-btn"
            disabled={busy || currentPage + 1 >= totalPages}
            onClick={() => onPage(currentPage + 1)}
            aria-label="Next page"
          >
            Next <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    );
  };

  const renderBlocklistTab = () => {
    const entries = blocklist?.entries || [];
    return (
      <div className="blr-browse">
        <form className="blr-filters" onSubmit={submitBlocklistSearch}>
          <div className="blr-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Search domain…"
              value={blocklistSearchInput}
              onChange={(e) => setBlocklistSearchInput(e.target.value)}
            />
          </div>
          <select
            value={blocklistFilters.active}
            onChange={(e) => applyBlocklistFilter({ active: e.target.value })}
          >
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Cleared</option>
          </select>
          <select
            value={blocklistFilters.source}
            onChange={(e) => applyBlocklistFilter({ source: e.target.value })}
          >
            <option value="">All sources</option>
            <option value="BATCH">Batch</option>
            <option value="ADMIN">Admin</option>
          </select>
          <button type="submit" className="blr-btn blr-btn-run">
            Search
          </button>
          <button
            type="button"
            className="blr-btn blr-btn-reject"
            onClick={resetBlocklistFilters}
          >
            Reset
          </button>
        </form>

        {blocklistLoading ? (
          <LoadingSpinner message="Loading blocklist…" />
        ) : entries.length === 0 ? (
          <div className="blr-empty">
            No blocklist entries match the filters.
          </div>
        ) : (
          <>
            <table className="blr-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>First confirmed</th>
                  <th>Last updated</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id}>
                    <td className="blr-domain">{row.domain}</td>
                    <td>
                      {row.active ? (
                        <span className="blr-tag blocked">Active</span>
                      ) : (
                        <span className="blr-tag safe">Cleared</span>
                      )}
                    </td>
                    <td>{row.source || "—"}</td>
                    <td>{formatDate(row.firstConfirmedAt)}</td>
                    <td>{formatDate(row.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPagination(
              blocklist,
              blocklistPage,
              setBlocklistPage,
              blocklistLoading,
            )}
          </>
        )}
      </div>
    );
  };

  const renderReportsTab = () => {
    const rows = reports?.reports || [];
    return (
      <div className="blr-browse">
        <form className="blr-filters" onSubmit={submitReportsSearch}>
          <div className="blr-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Search URL or domain…"
              value={reportsSearchInput}
              onChange={(e) => setReportsSearchInput(e.target.value)}
            />
          </div>
          <select
            value={reportsFilters.status}
            onChange={(e) => applyReportsFilter({ status: e.target.value })}
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="REVIEWING">Reviewing</option>
            <option value="AWAITING_ADMIN">Awaiting admin</option>
            <option value="CONFIRMED_MALICIOUS">Confirmed malicious</option>
            <option value="NOT_MALICIOUS">Not malicious</option>
            <option value="DISMISSED">Dismissed</option>
          </select>
          <select
            value={reportsFilters.reportType}
            onChange={(e) => applyReportsFilter({ reportType: e.target.value })}
          >
            <option value="">All types</option>
            <option value="SUSPICIOUS">Suspicious</option>
            <option value="KNOWN_BAD">Known bad</option>
            <option value="FALSE_POSITIVE">False positive</option>
          </select>
          <button type="submit" className="blr-btn blr-btn-run">
            Search
          </button>
          <button
            type="button"
            className="blr-btn blr-btn-reject"
            onClick={resetReportsFilters}
          >
            Reset
          </button>
        </form>

        {reportsLoading ? (
          <LoadingSpinner message="Loading reported URLs…" />
        ) : rows.length === 0 ? (
          <div className="blr-empty">No reported URLs match the filters.</div>
        ) : (
          <>
            <table className="blr-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Domain</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Reported</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="blr-sub-url" title={r.url}>
                      {r.url || "—"}
                    </td>
                    <td className="blr-domain">{r.domain || "—"}</td>
                    <td>{r.reportType || "—"}</td>
                    <td>{r.sourceChannel || "—"}</td>
                    <td>
                      <span className="blr-tag pending">{r.status || "—"}</span>
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPagination(
              reports,
              reportsPage,
              setReportsPage,
              reportsLoading,
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="blocklist-review">
      <div className="blr-header">
        <div className="blr-title">
          <FontAwesomeIcon icon={faShieldVirus} />
          <h2>Blocklist Review</h2>
        </div>
        <div className="blr-header-actions">
          {activeTab === "review" && (
            <button
              className="blr-btn blr-btn-run"
              onClick={handleRunBatch}
              disabled={refreshing}
            >
              <FontAwesomeIcon icon={faPlay} /> Run batch now
            </button>
          )}
          <RefreshButton onClick={handleRefresh} loading={refreshing} />
        </div>
      </div>

      <div className="blr-tabs" role="tablist">
        <button
          className={`blr-tab ${activeTab === "review" ? "active" : ""}`}
          role="tab"
          aria-selected={activeTab === "review"}
          onClick={() => setActiveTab("review")}
        >
          <FontAwesomeIcon icon={faClipboardCheck} /> Review queue
        </button>
        <button
          className={`blr-tab ${activeTab === "blocklist" ? "active" : ""}`}
          role="tab"
          aria-selected={activeTab === "blocklist"}
          onClick={() => setActiveTab("blocklist")}
        >
          <FontAwesomeIcon icon={faList} /> Blocklist
        </button>
        <button
          className={`blr-tab ${activeTab === "reports" ? "active" : ""}`}
          role="tab"
          aria-selected={activeTab === "reports"}
          onClick={() => setActiveTab("reports")}
        >
          <FontAwesomeIcon icon={faFlag} /> Reported URLs
        </button>
      </div>

      {activeTab === "blocklist" && renderBlocklistTab()}
      {activeTab === "reports" && renderReportsTab()}

      {activeTab === "review" && (
        <>
          <div className="blr-stats">
            <div className="blr-stat">
              <FontAwesomeIcon icon={faUsers} />
              <span className="blr-stat-value">
                {data?.activeUserCount ?? 0}
              </span>
              <span className="blr-stat-label">Active/subscribed users</span>
            </div>
            <div className="blr-stat">
              <span className="blr-stat-value">{data?.threshold ?? 0}</span>
              <span className="blr-stat-label">Report threshold (1%)</span>
            </div>
            <div className="blr-stat">
              <FontAwesomeIcon icon={faBan} />
              <span className="blr-stat-value">{data?.blocklistSize ?? 0}</span>
              <span className="blr-stat-label">Domains blocked</span>
            </div>
          </div>

          {/* Engine-proposed malicious domains → confirm / reject */}
          <section className="blr-section">
            <h3>
              Proposed malicious{" "}
              <span className="blr-count">{proposedBad.length}</span>
            </h3>
            <p className="blr-hint">
              Reported by at least {data?.threshold ?? 0} users and flagged by
              the engine re-scan. Confirm to add to the global blocklist.
            </p>
            {proposedBad.length === 0 ? (
              <div className="blr-empty">Nothing awaiting confirmation.</div>
            ) : (
              <table className="blr-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Domain</th>
                    <th>Reports</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Latest</th>
                    <th>Sample reasons</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {proposedBad.map((row) => {
                    const key = `bad:${row.domain}`;
                    const isOpen = !!expanded[key];
                    return (
                      <React.Fragment key={row.domain}>
                        <tr>
                          <td className="blr-expand-cell">
                            <button
                              className="blr-expand-btn"
                              aria-label={isOpen ? "Collapse" : "Expand"}
                              aria-expanded={isOpen}
                              onClick={() => toggleExpanded(key)}
                            >
                              <FontAwesomeIcon
                                icon={isOpen ? faChevronDown : faChevronRight}
                              />
                            </button>
                          </td>
                          <td className="blr-domain">{row.domain}</td>
                          <td>{row.reportCount}</td>
                          <td>{(row.reportTypes || []).join(", ") || "—"}</td>
                          <td>
                            {(row.sourceChannels || []).join(", ") || "—"}
                          </td>
                          <td>
                            {row.currentlyBlocked ? (
                              <span className="blr-tag blocked">Blocked</span>
                            ) : (
                              <span className="blr-tag pending">Awaiting</span>
                            )}
                          </td>
                          <td>{formatDate(row.latestReportedAt)}</td>
                          <td className="blr-reasons">
                            {(row.reasons || []).slice(0, 3).join("; ") || "—"}
                          </td>
                          <td className="blr-actions">
                            <button
                              className="blr-btn blr-btn-confirm"
                              disabled={busyDomain === row.domain}
                              onClick={() =>
                                runAction(
                                  (d) => AdminBlocklistAPI.confirm(d),
                                  row.domain,
                                  `Blocked ${row.domain}`,
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faCheck} /> Confirm
                            </button>
                            <button
                              className="blr-btn blr-btn-reject"
                              disabled={busyDomain === row.domain}
                              onClick={() =>
                                runAction(
                                  (d) => AdminBlocklistAPI.reject(d),
                                  row.domain,
                                  `Rejected ${row.domain}`,
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faTimes} /> Reject
                            </button>
                          </td>
                        </tr>
                        {renderDetailRow(row, "bad", 9)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          {/* User "actually safe" corrections → clear */}
          <section className="blr-section">
            <h3>
              "Actually safe" corrections{" "}
              <span className="blr-count">{falsePositives.length}</span>
            </h3>
            <p className="blr-hint">
              Users reported these as safe. These may be false positives —
              review and clear from the blocklist if warranted. Never
              auto-applied.
            </p>
            {falsePositives.length === 0 ? (
              <div className="blr-empty">No corrections to review.</div>
            ) : (
              <table className="blr-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Domain</th>
                    <th>Reports</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Latest</th>
                    <th>Sample reasons</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {falsePositives.map((row) => {
                    const key = `fp:${row.domain}`;
                    const isOpen = !!expanded[key];
                    return (
                      <React.Fragment key={row.domain}>
                        <tr>
                          <td className="blr-expand-cell">
                            <button
                              className="blr-expand-btn"
                              aria-label={isOpen ? "Collapse" : "Expand"}
                              aria-expanded={isOpen}
                              onClick={() => toggleExpanded(key)}
                            >
                              <FontAwesomeIcon
                                icon={isOpen ? faChevronDown : faChevronRight}
                              />
                            </button>
                          </td>
                          <td className="blr-domain">{row.domain}</td>
                          <td>{row.reportCount}</td>
                          <td>{(row.reportTypes || []).join(", ") || "—"}</td>
                          <td>
                            {(row.sourceChannels || []).join(", ") || "—"}
                          </td>
                          <td>
                            {row.currentlyBlocked ? (
                              <span className="blr-tag blocked">Blocked</span>
                            ) : (
                              <span className="blr-tag safe">Not blocked</span>
                            )}
                          </td>
                          <td>{formatDate(row.latestReportedAt)}</td>
                          <td className="blr-reasons">
                            {(row.reasons || []).slice(0, 3).join("; ") || "—"}
                          </td>
                          <td className="blr-actions">
                            <button
                              className="blr-btn blr-btn-clear"
                              disabled={busyDomain === row.domain}
                              onClick={() =>
                                runAction(
                                  (d) =>
                                    AdminBlocklistAPI.clearFalsePositive(d),
                                  row.domain,
                                  `Cleared ${row.domain}`,
                                )
                              }
                            >
                              <FontAwesomeIcon icon={faThumbsUp} /> Mark safe
                            </button>
                          </td>
                        </tr>
                        {renderDetailRow(row, "fp", 9)}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default AdminBlocklistReview;
